import styled from "@emotion/styled";

import { MDSIcon, MDSIconKind, MDSText, MDSTextColor, MDSTextVariant, MDSTextWeight } from "@/mds";
import { AgentLinkButton } from "@/pages/agent/components/agent-link-button";
import { AgentRowActionButton } from "@/pages/agent/components/agent-row-action-button";
import type { TaskDetailViewModel } from "@/pages/agent/task/agent-task-page.utils";

export const AgentTaskDetailCard = ({
  taskDetail,
  onCommentTask,
  onOpenProject,
  onOpenNextFollowUp,
}: {
  taskDetail: TaskDetailViewModel;
  onCommentTask: ({ taskId }: { taskId: string }) => void;
  onOpenProject: ({ projectId }: { projectId: string }) => void;
  onOpenNextFollowUp: ({ followUpId }: { followUpId: string }) => void;
}) => {
  const taskTitle = taskDetail.task.title || "Untitled task";
  const description = taskDetail.task.description?.trim();
  const nextFollowUp = taskDetail.nextFollowUp;
  const project = taskDetail.project;

  return (
    <StyledCard>
      <StyledCardHeader>
        {project ? (
          <StyledProjectChipButton
            type="button"
            onClick={() => {
              onOpenProject({ projectId: project.id });
            }}
          >
            <MDSIcon kind={MDSIconKind.Folder} size={12} />
            <span>{taskDetail.projectTitle}</span>
          </StyledProjectChipButton>
        ) : (
          <StyledProjectChipPlaceholder>
            <MDSIcon kind={MDSIconKind.Folder} size={12} />
            <span>{taskDetail.projectTitle}</span>
          </StyledProjectChipPlaceholder>
        )}

        <AgentRowActionButton
          aria-label={`Comment on ${taskTitle}`}
          iconKind={MDSIconKind.Message}
          iconSize={14}
          size={28}
          onClick={() => {
            onCommentTask({ taskId: taskDetail.task.id });
          }}
        />
      </StyledCardHeader>

      <StyledTitle
        color={MDSTextColor.Primary}
        variant={MDSTextVariant.HeaderM}
        weight={MDSTextWeight.SemiBold}
      >
        {taskTitle}
      </StyledTitle>

      <StyledDivider />

      {description ? (
        <StyledDescription color={MDSTextColor.Secondary} variant={MDSTextVariant.ParagraphS}>
          {description}
        </StyledDescription>
      ) : (
        <StyledDescription color={MDSTextColor.Tertiary} variant={MDSTextVariant.ParagraphS}>
          No description yet.
        </StyledDescription>
      )}

      <StyledNextFollowUpLine>
        <MDSText
          color={MDSTextColor.Primary}
          variant={MDSTextVariant.TextS}
          weight={MDSTextWeight.SemiBold}
        >
          Next follow-up:
        </MDSText>
        {nextFollowUp ? (
          <AgentLinkButton
            onClick={() => {
              onOpenNextFollowUp({ followUpId: nextFollowUp.id });
            }}
          >
            {nextFollowUp.content || "Untitled follow-up"}
          </AgentLinkButton>
        ) : (
          <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextS}>
            None scheduled
          </MDSText>
        )}
      </StyledNextFollowUpLine>
    </StyledCard>
  );
};

const StyledCard = styled.article`
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  min-width: 0;
  padding: ${({ theme }) => theme.spacing[5]};
`;

const StyledCardHeader = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: space-between;
  min-width: 0;
`;

const StyledProjectChipButton = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.colors.background.banner.default};
  border: 0;
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  font-size: ${({ theme }) => theme.fontSizes[MDSTextVariant.TextXs]};
  gap: ${({ theme }) => theme.spacing[1]};
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.background.banner.emphasis};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const StyledProjectChipPlaceholder = styled.span`
  align-items: center;
  background: ${({ theme }) => theme.colors.background.banner.default};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: inline-flex;
  font-size: ${({ theme }) => theme.fontSizes[MDSTextVariant.TextXs]};
  gap: ${({ theme }) => theme.spacing[1]};
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const StyledTitle = styled(MDSText)`
  overflow-wrap: anywhere;
`;

const StyledDivider = styled.div`
  background: ${({ theme }) => theme.colors.border.surface};
  height: 1px;
  width: 100%;
`;

const StyledDescription = styled(MDSText)`
  overflow-wrap: anywhere;
  white-space: pre-wrap;
`;

const StyledNextFollowUpLine = styled.div`
  align-items: baseline;
  display: flex;
  gap: ${({ theme }) => theme.spacing[1]};
  min-width: 0;
`;
